if (!customElements.get('hc-brands-directory')) {
  customElements.define('hc-brands-directory', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const {signal} = this.controller;
      const alphabet = this.querySelector('.hc-brands-directory-alphabet');
      const groups = this.querySelector('.hc-brands-directory-groups');
      const status = this.querySelector('[data-brand-status]');
      const retry = this.querySelector('[data-brand-retry]');
      const pagination = this.querySelector('.hc-brands-directory-pagination');
      const headers = [...document.querySelectorAll('.hc-category-navigation-section,.hc-mobile-search-section')];
      const measure = () => {
        let edge = 0;
        for (const header of headers) {
          const style = getComputedStyle(header);
          if (!['sticky', 'fixed'].includes(style.position)) continue;
          const height = header.getBoundingClientRect().height;
          if (height) edge = Math.max(edge, (parseFloat(style.top) || 0) + height);
        }
        this.style.setProperty('--hc-brands-top', `${Math.ceil(edge)}px`);
        this.style.setProperty('--hc-brands-alphabet', `${Math.ceil(alphabet.getBoundingClientRect().height)}px`);
      };
      this.observer?.disconnect();
      this.observer = new ResizeObserver(measure);
      [alphabet, ...headers].forEach(node => this.observer.observe(node));
      window.addEventListener('resize', measure, {passive: true, signal});
      const syncHash = () => {
        for (const link of alphabet.querySelectorAll('a')) {
          if (link.hash === location.hash) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        }
      };
      const finish = () => {
        const collator = new Intl.Collator(document.documentElement.lang || 'en', {sensitivity: 'base', numeric: true});
        for (const group of groups.querySelectorAll('[data-brand-group]')) {
          const grid = group.querySelector('ul');
          const cards = [...grid.children];
          cards.sort((a, b) => collator.compare(a.querySelector('.hc-brand-directory-title').textContent, b.querySelector('.hc-brand-directory-title').textContent));
          grid.replaceChildren(...cards);
          group.hidden = !cards.length;
          const link = alphabet.querySelector(`a[href="#${group.querySelector('h2').id}"]`);
          if (!cards.length) {
            link.setAttribute('aria-disabled', 'true');
            link.tabIndex = -1;
          }
        }
        status.textContent = '';
        retry.hidden = true;
        pagination.hidden = true;
        this.dataset.ready = 'true';
        measure();
      };
      const completed = new Set([Number(this.dataset.page)]);
      const known = new Set([...groups.querySelectorAll('[data-brand-handle]')].map(card => card.dataset.brandHandle));
      const load = async () => {
        status.textContent = this.dataset.loadingLabel;
        retry.hidden = true;
        const pages = Array.from({length: Number(this.dataset.pages)}, (_, i) => i + 1).filter(page => !completed.has(page));
        const worker = async () => {
          while (pages.length && !signal.aborted) {
            const page = pages.shift();
            const url = new URL(location.pathname, location.origin);
            url.searchParams.set('section_id', this.dataset.sectionId);
            url.searchParams.set('page', String(page));
            const view = new URL(location.href).searchParams.get('view');
            if (view) url.searchParams.set('view', view);
            const response = await fetch(url, {signal});
            if (!response.ok) throw new Error('Brand directory request failed');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const incoming = doc.querySelector('hc-brands-directory');
            if (!incoming || Number(incoming.dataset.page) !== page || Number(incoming.dataset.pages) !== Number(this.dataset.pages)) throw new Error('Incomplete brand directory response');
            if (signal.aborted) return;
            for (const card of incoming.querySelectorAll('[data-brand-handle]')) {
              if (known.has(card.dataset.brandHandle)) continue;
              const group = [...groups.querySelectorAll('[data-brand-group]')].find(group => group.dataset.brandGroup === card.dataset.brandLetter);
              if (!group) continue;
              known.add(card.dataset.brandHandle);
              group.querySelector('ul').append(document.importNode(card, true));
              group.hidden = false;
            }
            completed.add(page);
          }
        };
        const results = await Promise.allSettled(Array.from({length: Math.min(3, pages.length)}, worker));
        if (signal.aborted) return false;
        if (results.some(result => result.status === 'rejected')) {
          status.textContent = this.dataset.errorLabel;
          retry.hidden = false;
          return false;
        }
        finish();
        return true;
      };
      let loading = load();
      retry.addEventListener('click', () => { loading = load(); }, {signal});
      this.addEventListener('click', async event => {
        const link = event.target.closest('a[href^="#hc-brand-"]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (link.getAttribute('aria-disabled') === 'true') return;
        const target = document.getElementById(link.hash.slice(1));
        if (!target) return;
        if (target !== alphabet && !await loading) return;
        if (signal.aborted || !target.getClientRects().length) return;
        measure();
        history.pushState(null, '', link.hash);
        target.focus({preventScroll: true});
        const offset = parseFloat(this.style.getPropertyValue('--hc-brands-top')) + (target === alphabet ? 12 : alphabet.getBoundingClientRect().height + 24);
        window.scrollTo({top: target.getBoundingClientRect().top + scrollY - offset, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        syncHash();
      }, {signal});
      window.addEventListener('hashchange', syncHash, {signal});
      measure();
      syncHash();
      loading.then(ready => {
        if (!ready || signal.aborted || !location.hash.startsWith('#hc-brand-heading-')) return;
        const target = document.getElementById(location.hash.slice(1));
        if (target?.getClientRects().length) window.scrollTo({top: target.getBoundingClientRect().top + scrollY - parseFloat(this.style.getPropertyValue('--hc-brands-top')) - alphabet.getBoundingClientRect().height - 24, behavior: 'instant'});
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
